// Measurement math + formatting for the calibration / measure tools.
//
// All raw coordinates in the markup system are PDF page-points (1/72 inch at
// the sheet's published paper size). A calibration binds those points to a
// real-world foot count: `pagePointsPerFoot`. Every formatter below takes a
// calibration object and converts.
//
// Calibration shape:
//   {
//     pagePointsPerFoot: number,       // primary scale factor
//     lengthFormat: 'decimal-ft' | 'decimal-in' | 'architectural',
//     method: 'preset' | 'two-point',
//     label: string,                   // human-readable, e.g. '1/4" = 1'-0"'
//     reference?: { x1,y1,x2,y2, realValue, realUnit },  // 2-point pick
//     setAt: ISO string,
//     setBy: string
//   }

// ── Scale presets ───────────────────────────────────────────────────────────
// pagePointsPerFoot = 72 * paperInchesPerFoot. Derivation:
//   "1/4\" = 1'-0\"" means 0.25 paper-inches represents 1 real foot.
//   1 paper inch = 72 PDF points → 1 real foot = 0.25 × 72 = 18 PDF points.

export const SCALE_PRESETS = [
  // Architectural (fractional inch per foot)
  { group: 'Architectural', label: '1/16" = 1\'-0"',   paperInPerFoot: 1/16 },
  { group: 'Architectural', label: '3/32" = 1\'-0"',   paperInPerFoot: 3/32 },
  { group: 'Architectural', label: '1/8" = 1\'-0"',    paperInPerFoot: 1/8  },
  { group: 'Architectural', label: '3/16" = 1\'-0"',   paperInPerFoot: 3/16 },
  { group: 'Architectural', label: '1/4" = 1\'-0"',    paperInPerFoot: 1/4  },
  { group: 'Architectural', label: '3/8" = 1\'-0"',    paperInPerFoot: 3/8  },
  { group: 'Architectural', label: '1/2" = 1\'-0"',    paperInPerFoot: 1/2  },
  { group: 'Architectural', label: '3/4" = 1\'-0"',    paperInPerFoot: 3/4  },
  { group: 'Architectural', label: '1" = 1\'-0"',      paperInPerFoot: 1    },
  { group: 'Architectural', label: '1-1/2" = 1\'-0"',  paperInPerFoot: 1.5  },
  { group: 'Architectural', label: '3" = 1\'-0"',      paperInPerFoot: 3    },
  // Engineering (1 inch on paper = N feet in reality)
  { group: 'Engineering',   label: '1" = 10\'',        paperInPerFoot: 1/10 },
  { group: 'Engineering',   label: '1" = 20\'',        paperInPerFoot: 1/20 },
  { group: 'Engineering',   label: '1" = 30\'',        paperInPerFoot: 1/30 },
  { group: 'Engineering',   label: '1" = 40\'',        paperInPerFoot: 1/40 },
  { group: 'Engineering',   label: '1" = 50\'',        paperInPerFoot: 1/50 },
  { group: 'Engineering',   label: '1" = 60\'',        paperInPerFoot: 1/60 },
  { group: 'Engineering',   label: '1" = 80\'',        paperInPerFoot: 1/80 },
  { group: 'Engineering',   label: '1" = 100\'',       paperInPerFoot: 1/100 },
  { group: 'Engineering',   label: '1" = 200\'',       paperInPerFoot: 1/200 },
];

export const LENGTH_FORMATS = [
  { id: 'decimal-ft',    label: 'Decimal feet',       sample: '12.50 ft' },
  { id: 'architectural', label: 'Feet-inches',        sample: '12\'-6"' },
  { id: 'decimal-in',    label: 'Decimal inches',     sample: '150.00 in' },
];

export const DISTANCE_UNITS = [
  { id: 'ft', label: 'feet',   toFeet: 1 },
  { id: 'in', label: 'inches', toFeet: 1 / 12 },
  { id: 'yd', label: 'yards',  toFeet: 3 },
];

// ── Preset → ppf conversion ─────────────────────────────────────────────────
export function ppfFromPreset(paperInPerFoot) {
  return 72 * paperInPerFoot;
}

// ── Two-point calibration → ppf ─────────────────────────────────────────────
// pageDist: Euclidean distance between the two picks in PDF points.
// realValue + realUnit: user-entered real-world distance between those points.
export function ppfFromTwoPoint(pageDist, realValue, realUnit) {
  const unit = DISTANCE_UNITS.find((u) => u.id === realUnit) || DISTANCE_UNITS[0];
  const realFeet = realValue * unit.toFeet;
  if (!realFeet || !pageDist) return 0;
  return pageDist / realFeet;
}

// ── Geometry helpers ────────────────────────────────────────────────────────
export function distance(a, b) {
  const dx = b.x - a.x, dy = b.y - a.y;
  return Math.sqrt(dx * dx + dy * dy);
}

export function polylineLength(points) {
  if (!points || points.length < 2) return 0;
  let total = 0;
  for (let i = 1; i < points.length; i++) {
    total += distance(points[i - 1], points[i]);
  }
  return total;
}

// Shoelace formula — returns absolute area of a polygon given an ordered point
// list. Works for simple polygons (self-intersecting shapes produce a signed
// sum which can under-report area, but field use is non-self-intersecting).
export function polygonArea(points) {
  if (!points || points.length < 3) return 0;
  let sum = 0;
  const n = points.length;
  for (let i = 0; i < n; i++) {
    const a = points[i];
    const b = points[(i + 1) % n];
    sum += a.x * b.y - b.x * a.y;
  }
  return Math.abs(sum) / 2;
}

// Centroid of a polygon (signed-area weighted). Falls back to the vertex
// average for degenerate shapes.
export function polygonCentroid(points) {
  if (!points || points.length < 3) {
    if (!points?.length) return { x: 0, y: 0 };
    const sx = points.reduce((a, p) => a + p.x, 0) / points.length;
    const sy = points.reduce((a, p) => a + p.y, 0) / points.length;
    return { x: sx, y: sy };
  }
  let cx = 0, cy = 0, a2 = 0;
  const n = points.length;
  for (let i = 0; i < n; i++) {
    const p0 = points[i];
    const p1 = points[(i + 1) % n];
    const cross = p0.x * p1.y - p1.x * p0.y;
    a2 += cross;
    cx += (p0.x + p1.x) * cross;
    cy += (p0.y + p1.y) * cross;
  }
  if (a2 === 0) {
    const sx = points.reduce((a, p) => a + p.x, 0) / n;
    const sy = points.reduce((a, p) => a + p.y, 0) / n;
    return { x: sx, y: sy };
  }
  return { x: cx / (3 * a2), y: cy / (3 * a2) };
}

// ── Formatters ──────────────────────────────────────────────────────────────
export function formatLength(pagePoints, calibration) {
  if (!calibration || !calibration.pagePointsPerFoot) {
    return `${Math.round(pagePoints)} pt`;
  }
  const feet = pagePoints / calibration.pagePointsPerFoot;
  switch (calibration.lengthFormat) {
    case 'architectural': return formatArchitectural(feet);
    case 'decimal-in':    return `${(feet * 12).toFixed(2)} in`;
    case 'decimal-ft':
    default:              return `${feet.toFixed(2)} ft`;
  }
}

// Feet-inches with 1/16" precision. Simplifies the fractional part.
// Examples: 12.5 ft → 12'-6"; 12.375 ft → 12'-4 1/2"; 0.0625 ft → 3/4".
export function formatArchitectural(feet) {
  if (feet < 0) return '-' + formatArchitectural(-feet);
  const totalSixteenths = Math.round(feet * 12 * 16);
  const feetWhole = Math.floor(totalSixteenths / (12 * 16));
  const remainder = totalSixteenths - feetWhole * 12 * 16;
  const inchesWhole = Math.floor(remainder / 16);
  const fracSixteenths = remainder - inchesWhole * 16;

  let fracStr = '';
  if (fracSixteenths !== 0) {
    const { num, den } = simplifyFraction(fracSixteenths, 16);
    fracStr = `${num}/${den}`;
  }

  if (feetWhole === 0 && inchesWhole === 0 && fracStr) return `${fracStr}"`;
  if (feetWhole === 0) {
    return fracStr ? `${inchesWhole} ${fracStr}"` : `${inchesWhole}"`;
  }
  const inchPart = fracStr
    ? `${inchesWhole ? inchesWhole + ' ' : ''}${fracStr}"`
    : `${inchesWhole}"`;
  if (inchesWhole === 0 && !fracStr) return `${feetWhole}'`;
  return `${feetWhole}'-${inchPart}`;
}

function simplifyFraction(num, den) {
  const g = gcd(num, den);
  return { num: num / g, den: den / g };
}
function gcd(a, b) { return b === 0 ? a : gcd(b, a % b); }

// Areas are always shown as sq ft plus sq yd in parens.
export function formatArea(pagePointsSq, calibration) {
  if (!calibration || !calibration.pagePointsPerFoot) {
    return `${Math.round(pagePointsSq)} pt²`;
  }
  const ppf = calibration.pagePointsPerFoot;
  const sqFt = pagePointsSq / (ppf * ppf);
  const sqYd = sqFt / 9;
  return `${sqFt.toFixed(2)} sq ft  (${sqYd.toFixed(2)} sq yd)`;
}

// Volumes are always shown as cu ft plus cu yd in parens. Depth in feet.
export function formatVolume(pagePointsSq, depthFt, calibration) {
  if (!calibration || !calibration.pagePointsPerFoot) return '—';
  const ppf = calibration.pagePointsPerFoot;
  const sqFt = pagePointsSq / (ppf * ppf);
  const cuFt = sqFt * depthFt;
  const cuYd = cuFt / 27;
  return `${cuFt.toFixed(2)} cu ft  (${cuYd.toFixed(2)} cu yd)`;
}

// Short form for the toolbar scale chip.
export function scaleChipLabel(calibration) {
  if (!calibration) return 'No scale';
  return calibration.label || 'Calibrated';
}
